import React, { useState, useEffect } from 'react';
import './About.css';

function About() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const setup = [
    { name: 'GPU', value: 'RTX 4090 (MSI Gaming X Trio)' },
    { name: 'CPU', value: 'Intel i9-14900K' },
    { name: 'RAM', value: '64GB DDR5-6000' },
    { name: 'Storage', value: '2TB Samsung 990 Pro NVMe' },
    { name: 'Monitor', value: 'LG 27GP950 (4K 144Hz)' },
    { name: 'Mouse', value: 'Logitech G Pro X Superlight 2' },
    { name: 'Keyboard', value: 'Wooting 60HE' },
    { name: 'Headset', value: 'SteelSeries Arctis Nova Pro' }
  ];

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile`);
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <main className="about-page" data-testid="about-page">
      <div className="container">
        {/* Hero Section */}
        <section className="about-hero" data-testid="about-hero">
          <div className="about-hero-content">
            <div className="avatar-section" data-testid="avatar-section">
              <div className="avatar-wrapper">
                {profile?.avatar ? (
                  <img src={profile.avatar} alt={profile.name} className="avatar-image" />
                ) : (
                  <div className="avatar-placeholder">
                    🎮
                  </div>
                )}
              </div>
            </div>
            <div className="hero-text">
              <h1 className="about-title brand-text" data-testid="about-title">
                {profile?.name || 'ClutchBabe9'}
              </h1>
              <p className="about-tagline" data-testid="about-tagline">
                Competitive Gamer | Content Creator | RPG Enthusiast
              </p>
            </div>
          </div>
        </section>

        {/* Bio Section */}
        <section className="bio-section" data-testid="bio-section">
          <div className="bio-content glass-card">
            <h2 className="section-title">Who Am I?</h2>
            <div className="bio-text">
              {profile?.bio ? (
                profile.bio.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))
              ) : (
                <>
                  <p>
                    Hey there! I'm ClutchBabe9, and I'm all about <strong>coming in clutch</strong> when it matters most. 
                    Whether it's pulling off that perfect combo in a boss fight, finding hidden secrets in massive RPGs, 
                    or helping fellow gamers level up their skills – I live for those epic gaming moments.
                  </p>
                  <p>
                    I've been gaming competitively for years, diving deep into everything from sprawling RPGs like 
                    <em> Elden Ring</em> and <em>Baldur's Gate 3</em> to fast-paced competitive shooters. 
                    My content focuses on <strong>honest reviews</strong>, <strong>detailed walkthroughs</strong>, 
                    and <strong>practical tips</strong> that actually help you improve.
                  </p>
                  <p>
                    When I'm not grinding through a new game, I'm creating content for YouTube, streaming on Twitch, 
                    and sharing quick clips on TikTok and Instagram. I believe gaming should be fun for everyone – 
                    whether you're a hardcore min-maxer or a casual player just enjoying the story.
                  </p>
                </>
              )}
            </div>
          </div>
        </section>

        {/* What Clutch Mode Means */}
        <section className="clutch-mode-section" data-testid="clutch-mode-section">
          <div className="clutch-mode-card glass-card">
            <div className="clutch-icon">🔥</div>
            <h2 className="section-title">What Does "Clutch Mode" Mean?</h2>
            <p className="clutch-description">
              In gaming, "clutch" is that moment when everything's on the line. Your team is down, 
              the boss has 1% HP left, or you're in a 1v5 situation – and you <strong>still pull through</strong>. 
              It's about staying cool under pressure, making the right call, and executing perfectly when it counts.
            </p>
            <p className="clutch-description">
              That's the energy I bring to every guide, review, and tip I share. No fluff, no clickbait – 
              just real, actionable content to help you activate your own clutch mode. 💪
            </p>
          </div>
        </section>

        {/* Gaming Setup */}
        <section className="setup-section" data-testid="setup-section">
          <h2 className="section-title">My Gaming Setup</h2>
          <p className="section-description">
            Here's the gear I use to create content and dominate the competition:
          </p>
          <div className="setup-grid">
            {setup.map((item, index) => (
              <div key={index} className="setup-item glass-card" data-testid={`setup-item-${index}`}>
                <div className="setup-name">{item.name}</div>
                <div className="setup-value">{item.value}</div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="about-cta-section" data-testid="about-cta">
          <div className="about-cta-card glass-card">
            <h2 className="cta-title">Let's Stay Connected</h2>
            <p className="cta-description">
              Follow me on social media for daily gaming content, behind-the-scenes looks, and community discussions!
            </p>
            {profile?.social_links && profile.social_links.length > 0 && (
              <div className="cta-buttons">
                {profile.social_links.map((link, index) => (
                  <a 
                    key={index}
                    href={link.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={index === 0 ? "btn btn-primary" : "btn btn-secondary"}
                    data-testid={`cta-${link.platform.toLowerCase()}`}
                  >
                    {link.platform}
                  </a>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}

export default About;
